import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useList } from '../hooks/useList';
import { DataTable, type Column } from '../components/ui/DataTable';
import { PageHeader, Badge, Select, Money, DateText, Button, Modal, Input, Card, KeyValue } from '../components/ui';
import { Can } from '../components/Can';

type P = Record<string, any>;

/**
 * Money in and money out, and — the part that matters — WHAT IT SETTLED.
 *
 * A payment on its own is just cash. Allocation is the separate decision that says
 * which invoices or bills it clears, which is why one transfer can settle three
 * documents and an overpayment can sit on account until something absorbs it.
 */
export default function PaymentsPage() {
  const qc = useQueryClient();
  const list = useList<P>('/payments');
  const [open, setOpen] = useState<P | null>(null);
  const [allocating, setAllocating] = useState<P | null>(null);
  const [picks, setPicks] = useState<Record<string, string>>({});

  const detail = useQuery({ queryKey: ['payment', open?.id], queryFn: async () => (await api.get<P>(`/payments/${open!.id}`)).data, enabled: !!open });
  const unapplied = useQuery({ queryKey: ['unapplied-payments'], queryFn: async () => (await api.get<P[]>('/allocations/unapplied')).data });
  const openDocs = useQuery({
    queryKey: ['allocation-targets', allocating?.id, allocating?.direction],
    queryFn: async () => {
      const path = allocating!.direction === 'OUT' ? '/bills' : '/invoices';
      return (await api.get<P[]>(path, { outstandingOnly: true, perPage: 100, entityId: allocating!.entityId })).data;
    },
    enabled: !!allocating,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['/payments'] });
    void qc.invalidateQueries({ queryKey: ['unapplied-payments'] });
    if (open) void qc.invalidateQueries({ queryKey: ['payment', open.id] });
  };

  const allocate = useMutation({
    mutationFn: async () => api.post(`/payments/${allocating!.id}/allocate`, {
      allocations: Object.entries(picks).flatMap(([targetId, value]) => {
        const amount = Math.round(Number(value) * 100);
        return amount > 0 ? [{ targetType: allocating!.direction === 'OUT' ? 'BILL' : 'INVOICE', targetId, amountMinor: amount }] : [];
      }),
    }),
    onSuccess: () => { setAllocating(null); setPicks({}); refresh(); },
  });
  const unallocate = useMutation({ mutationFn: async (id: string) => api.delete(`/allocations/${id}`), onSuccess: refresh });

  const columns: Column<P>[] = [
    { key: 'reference', label: 'Reference', render: (p) => <div><span className="font-mono text-xs text-ink-600">{p.reference}</span><div className="text-[11px] text-ink-400">{p.direction === 'OUT' ? 'paid out' : 'received'}</div></div> },
    { key: 'payerName', label: 'Counterparty', render: (p) => <div><div className="font-medium text-ink-800">{p.payerName || p.vendorName || '—'}</div><div className="text-xs text-ink-400">{p.invoiceNumber ?? p.tenancyReference ?? '—'}</div></div> },
    { key: 'receivedOn', label: 'Date', render: (p) => <DateText value={p.receivedOn} /> },
    { key: 'method', label: 'Method', render: (p) => <Badge value={p.method} tone="neutral" />, hideOnMobile: true },
    { key: 'amountMinor', label: 'Amount', align: 'right', render: (p) => <Money minor={p.amountMinor} currency={p.currency} /> },
    { key: 'unallocatedMinor', label: 'On account', align: 'right', render: (p) => (p.unallocatedMinor > 0 ? <Money minor={p.unallocatedMinor} currency={p.currency} className="font-medium text-amber-700" /> : <span className="text-ink-300">—</span>) },
    { key: 'isReconciled', label: 'Bank', render: (p) => (p.isReconciled ? <Badge value="Reconciled" tone="good" /> : <Badge value="Unreconciled" tone="warning" />), hideOnMobile: true },
    { key: 'status', label: 'Status', render: (p) => <Badge value={p.status} /> },
    {
      key: 'actions', label: '', render: (p) => (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          {p.unallocatedMinor > 0 && p.status === 'CLEARED' && <Can do="payments.allocate"><Button size="sm" onClick={() => { setPicks({}); setAllocating(p); }}>Allocate</Button></Can>}
        </div>),
    },
  ];

  const remaining = allocating
    ? allocating.unallocatedMinor - Object.values(picks).reduce((s, v) => s + Math.round((Number(v) || 0) * 100), 0)
    : 0;

  return (
    <div>
      <PageHeader title="Payments" subtitle="Every receipt and supplier payment, what it settled, and whether it has been matched to the bank." />

      {!!unapplied.data?.length && (
        <Card className="mb-5" title="Money on account" subtitle="Cleared but not yet allocated to a document — a real state a ledger has to hold, not an error">
          <ul className="space-y-1.5 text-[13px]">
            {unapplied.data.slice(0, 6).map((u) => (
              <li key={u.id} className="flex items-center justify-between">
                <span className="text-ink-700"><span className="font-mono text-[12px] text-ink-500">{u.reference}</span> {u.payerName ?? '—'} <span className="text-ink-400"><DateText value={u.receivedOn} /></span></span>
                <span className="flex items-center gap-3"><Money minor={u.unallocatedMinor} currency={u.currency} className="font-medium" /><Can do="payments.allocate"><Button size="sm" variant="ghost" onClick={() => { setPicks({}); setAllocating(u); }}>Allocate</Button></Can></span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <DataTable
        columns={columns} rows={list.rows} meta={list.meta} page={list.page} onPage={list.setPage}
        isLoading={list.isLoading} isFetching={list.isFetching} rowKey={(p) => p.id} onRowClick={setOpen}
        search={list.search} onSearch={list.setSearch} searchPlaceholder="Reference, bank reference, invoice…"
        filters={<>
          <Select value={String(list.filters.direction ?? '')} onChange={(e) => list.setFilter('direction', e.target.value)}>
            <option value="">In and out</option><option value="IN">Received</option><option value="OUT">Paid out</option>
          </Select>
          <Select value={String(list.filters.method ?? '')} onChange={(e) => list.setFilter('method', e.target.value)}>
            <option value="">Any method</option>{['BANK_TRANSFER', 'DIRECT_DEBIT', 'CARD', 'CHEQUE', 'CASH', 'STRIPE'].map((s) => <option key={s}>{s}</option>)}
          </Select>
          <Select value={String(list.filters.isReconciled ?? '')} onChange={(e) => list.setFilter('isReconciled', e.target.value === '' ? undefined : e.target.value === 'true')}>
            <option value="">Reconciled or not</option><option value="false">Unreconciled</option><option value="true">Reconciled</option>
          </Select>
          <label className="flex items-center gap-1.5 text-xs text-ink-600"><input type="checkbox" checked={!!list.filters.unappliedOnly} onChange={(e) => list.setFilter('unappliedOnly', e.target.checked || undefined)} /> On account only</label>
        </>}
      />

      {/* Detail */}
      <Modal open={!!open} onClose={() => setOpen(null)} size="lg" title={detail.data?.reference ?? open?.reference ?? 'Payment'} footer={detail.data && detail.data.unallocatedMinor > 0 && (
        <Can do="payments.allocate"><Button onClick={() => { setPicks({}); setAllocating(detail.data!); }}>Allocate the balance</Button></Can>
      )}>
        {detail.data && (
          <div className="space-y-4">
            <div className="flex items-center justify-between"><Badge value={detail.data.status} /><span className="text-xs text-ink-400">{detail.data.direction === 'OUT' ? 'Paid out' : 'Received'} · {String(detail.data.paymentType).replace(/_/g, ' ').toLowerCase()}</span></div>
            <KeyValue items={[
              { label: 'Counterparty', value: detail.data.payerName || detail.data.vendorName || '—' },
              { label: 'Date', value: <DateText value={detail.data.receivedOn} /> },
              { label: 'Amount', value: <Money minor={detail.data.amountMinor} currency={detail.data.currency} /> },
              { label: 'Unallocated', value: <Money minor={detail.data.unallocatedMinor} currency={detail.data.currency} /> },
              { label: 'Method', value: String(detail.data.method).replace(/_/g, ' ').toLowerCase() },
              { label: 'Bank reference', value: detail.data.externalReference ?? '—' },
            ]} />

            <Card title="What it settled">
              {!detail.data.allocations?.length ? (
                <p className="text-[13px] text-ink-500">Nothing yet — the whole amount is sitting on account.</p>
              ) : (
                <ul className="space-y-1.5 text-[12.5px]">
                  {detail.data.allocations.map((a: P) => (
                    <li key={a.id} className="flex items-center justify-between">
                      <span className="text-ink-700">{a.targetReference} <span className="text-ink-400">({a.targetType.toLowerCase()})</span></span>
                      <span className="flex items-center gap-3">
                        <span className="text-ink-400"><DateText value={a.allocatedOn} /></span>
                        <Money minor={a.amountMinor} currency={a.currency} />
                        <Can do="payments.allocate"><button onClick={() => unallocate.mutate(a.id)} className="text-[11px] text-ink-400 hover:text-red-600">undo</button></Can>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            {detail.data.journalId && <p className="text-[11px] text-ink-400">Posted to the ledger — see Journals for the entry.</p>}
          </div>
        )}
      </Modal>

      {/* Allocate */}
      <Modal open={!!allocating} onClose={() => { setAllocating(null); setPicks({}); }} size="lg" title={`Allocate ${allocating?.reference ?? ''}`} footer={<>
        <Button variant="secondary" onClick={() => { setAllocating(null); setPicks({}); }}>Cancel</Button>
        <Button onClick={() => allocate.mutate()} disabled={allocate.isPending || remaining < 0 || !Object.values(picks).some((v) => Number(v) > 0)}>Allocate</Button>
      </>}>
        {allocating && (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-xl bg-ink-50 px-3.5 py-3 text-[13px]">
              <span className="text-ink-600">Left to allocate</span>
              <span className={`font-semibold ${remaining < 0 ? 'text-red-600' : ''}`}><Money minor={remaining} currency={allocating.currency} /></span>
            </div>
            {remaining < 0 && <p className="text-xs text-red-600">You have allocated more than the payment is worth.</p>}

            {!openDocs.data?.length ? (
              <p className="text-[13px] text-ink-500">Nothing outstanding to allocate against on this company.</p>
            ) : (
              <table className="w-full text-[13px]">
                <thead><tr className="border-b border-ink-100 text-left text-[11px] uppercase text-ink-400">
                  <th className="py-1.5">{allocating.direction === 'OUT' ? 'Bill' : 'Invoice'}</th><th className="py-1.5">Due</th><th className="py-1.5 text-right">Outstanding</th><th className="py-1.5 text-right w-32">Apply</th>
                </tr></thead>
                <tbody>
                  {openDocs.data.map((d) => (
                    <tr key={d.id} className="border-b border-ink-50 last:border-0">
                      <td className="py-1.5"><span className="font-mono text-[12px] text-ink-600">{d.number}</span><div className="text-[11px] text-ink-400">{d.customerName || d.vendorName || '—'}</div></td>
                      <td className="py-1.5 text-ink-500"><DateText value={d.dueDate} /></td>
                      <td className="py-1.5 text-right"><Money minor={d.outstandingMinor} currency={d.currency} /></td>
                      <td className="py-1.5 pl-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Input type="number" step="0.01" className="w-24 text-right" value={picks[d.id] ?? ''} onChange={(e) => setPicks({ ...picks, [d.id]: e.target.value })} placeholder="0.00" />
                          <button className="text-[11px] text-bronze-600 hover:underline"
                            onClick={() => setPicks({ ...picks, [d.id]: (Math.min(d.outstandingMinor, remaining + Math.round((Number(picks[d.id]) || 0) * 100)) / 100).toFixed(2) })}>
                            max
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {allocate.isError && <p className="text-xs text-red-600">{(allocate.error as Error).message}</p>}
          </div>
        )}
      </Modal>

      {/* Allocation errors surfaced outside the modal too */}
      {unallocate.isError && <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{(unallocate.error as Error).message}</p>}
    </div>
  );
}
